'use client';

/**
 * ContactSelector - Searchable contact dropdown with manual entry option
 *
 * Reusable component for selecting contacts to message. Supports:
 * - Search/filter by name, email, or phone
 * - Display selected contact with clear button
 * - Manual phone number entry fallback
 *
 * Used by ProjectChat, RFQChat, and VendorChat for WhatsApp messaging.
 *
 * @component_role Contact Selection
 * @business_rule Filters contacts by name/email/phone. Shows contact type badge.
 *   Dims contacts without phone numbers. Allows manual entry for unlisted numbers.
 * @user_outcome Quick contact lookup - find the right person to message without
 *   scrolling through long lists.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useState } from 'react';
import { XCircle } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Input } from '@/components/ui/input';
import type { ContactSummary } from '@/lib/api/types';

interface ContactSelectorProps {
  contacts: ContactSummary[];
  selectedContact: ContactSummary | null;
  onSelect: (contact: ContactSummary | null) => void;
  onManualEntry: () => void;
  isLoading?: boolean;
  placeholder?: string;
  maxDisplay?: number;
}

export function ContactSelector({
  contacts,
  selectedContact,
  onSelect,
  onManualEntry,
  isLoading = false,
  placeholder = 'Search contacts...',
  maxDisplay = 10,
}: ContactSelectorProps) {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Selected contact display
  if (selectedContact) {
    return (
      <div className="flex items-center gap-2 h-8 px-2 text-sm rounded-md border border-input bg-background">
        <span className="flex-1 truncate">
          {selectedContact.name}
          {selectedContact.phone && (
            <span className="text-muted-foreground ml-1">({selectedContact.phone})</span>
          )}
        </span>
        <button
          onClick={() => onSelect(null)}
          className="text-muted-foreground hover:text-foreground"
        >
          <XCircle className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Filter contacts by search term
  const searchLower = search.toLowerCase();
  const filtered = contacts.filter(
    (c) =>
      c.name.toLowerCase().includes(searchLower) ||
      c.email?.toLowerCase().includes(searchLower) ||
      c.phone?.includes(search)
  );

  return (
    <div className="relative">
      <Input
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
        placeholder={isLoading ? 'Loading...' : placeholder}
        className="h-8 text-sm"
        disabled={isLoading}
      />

      {showDropdown && (
        <>
          <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto rounded-md border border-input bg-background shadow-lg">
            {filtered.length === 0 && search && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No contacts found</div>
            )}

            {filtered.slice(0, maxDisplay).map((contact) => (
              <button
                key={contact.semantic_id}
                onClick={() => {
                  onSelect(contact);
                  setShowDropdown(false);
                  setSearch('');
                }}
                className={cn(
                  'w-full px-2 py-1.5 text-left text-sm hover:bg-accent flex items-center justify-between',
                  !contact.phone && 'opacity-60'
                )}
              >
                <span className="truncate">
                  {contact.name}
                  {contact.type && (
                    <span className="ml-1 text-[10px] text-muted-foreground">
                      [{contact.type.replace('_', ' ')}]
                    </span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground ml-2 shrink-0">
                  {contact.phone || 'no phone'}
                </span>
              </button>
            ))}

            {filtered.length > maxDisplay && (
              <div className="px-2 py-1 text-xs text-muted-foreground border-t">
                +{filtered.length - maxDisplay} more...
              </div>
            )}

            <button
              onClick={() => {
                onManualEntry();
                setShowDropdown(false);
                setSearch('');
              }}
              className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent text-primary border-t"
            >
              Enter phone manually...
            </button>
          </div>

          {/* Backdrop to close dropdown */}
          <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
        </>
      )}
    </div>
  );
}

// ============================================================
// Simple Contact List (for RFQ/Vendor chat where full search not needed)
// ============================================================

interface ContactListProps {
  contacts: ContactSummary[];
  selectedContact: ContactSummary | null;
  onSelect: (contact: ContactSummary) => void;
  isLoading?: boolean;
  maxDisplay?: number;
  emptyMessage?: string;
}

export function ContactList({
  contacts,
  selectedContact,
  onSelect,
  isLoading = false,
  maxDisplay = 5,
  emptyMessage = 'No contacts available',
}: ContactListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        Loading contacts...
      </div>
    );
  }

  if (contacts.length === 0) {
    return <div className="text-sm text-muted-foreground">{emptyMessage}</div>;
  }

  return (
    <div className="space-y-1">
      {contacts.slice(0, maxDisplay).map((contact) => (
        <ContactListItem
          key={contact.semantic_id}
          contact={contact}
          isSelected={selectedContact?.semantic_id === contact.semantic_id}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

interface ContactListItemProps {
  contact: ContactSummary;
  isSelected: boolean;
  onSelect: (contact: ContactSummary) => void;
}

function ContactListItem({ contact, isSelected, onSelect }: ContactListItemProps) {
  return (
    <button
      onClick={() => onSelect(contact)}
      className={cn(
        'w-full p-2 rounded-lg text-left transition-colors text-sm',
        isSelected
          ? 'bg-primary/10 border border-primary/20'
          : 'hover:bg-accent border border-transparent',
        !contact.phone && 'opacity-60'
      )}
    >
      <div className="flex items-center gap-2">
        <div className="h-3.5 w-3.5 rounded-full bg-muted-foreground/20" />
        <span className="font-medium">{contact.name}</span>
        {contact.type && (
          <span className="text-[10px] text-muted-foreground">[{contact.type.replace('_', ' ')}]</span>
        )}
      </div>
      <div className="text-xs text-muted-foreground ml-5 mt-0.5">
        {contact.phone || 'No phone'}
        {contact.email && ` • ${contact.email}`}
      </div>
    </button>
  );
}
